import React, { useMemo } from "react";
import ReportStatus from "pages/finance/filters/components/ReportStatus/ReportStatus";
import { REPORT_TYPE_OPTIONS } from "pages/finance/filters/components/ReportTypeHeaderSelector/ReportTypeHeaderSelector";

import BatchInvoiceActions from "./BatchInvoiceActions";
import Section111InvoiceActions from "./Section111InvoiceActions";
import { useReportActions } from "../contexts/ReportActionsContext";

// renders finance report status indicator in the header (loading state, last run time, record count)
// also renders batch invoice actions for invoice list reports
// reads all data from Context (properly memoized)
const FinanceReportHeaderStatus: React.FC = () => {
  const { state } = useReportActions();

  const {
    isLoadingReportData,
    lastRunTime,
    reportType: reportTypeValue,
    showReportTable,
    reportDataJson,
    reportMetaJson,
    selectedRowsArray,
    setSelectedRows,
    totalRecords,
  } = state;

  // find the matching report type option for display
  const reportType = reportTypeValue
    ? REPORT_TYPE_OPTIONS.find((opt) => opt.value === reportTypeValue) || null
    : null;

  // deserialize report data from JSON
  const reportData = useMemo(() => {
    if (!reportDataJson) return [];
    try {
      return JSON.parse(reportDataJson) as Record<string, unknown>[];
    } catch (e) {
      console.error("Failed to parse report data:", e);
      return [];
    }
  }, [reportDataJson]);

  // deserialize report meta from JSON
  const reportMeta = useMemo(() => {
    if (!reportMetaJson) return null;
    try {
      return JSON.parse(reportMetaJson) as Record<string, unknown>;
    } catch (e) {
      console.error("Failed to parse report meta:", e);
      return null;
    }
  }, [reportMetaJson]);

  // convert array back to Set
  const selectedRows = useMemo(() => {
    return new Set(selectedRowsArray);
  }, [selectedRowsArray]);

  // check if we should show batch actions
  const canShowInvoiceListBatch =
    reportTypeValue === "INVOICE_LIST" &&
    showReportTable &&
    !isLoadingReportData;

  const canShowSection111Batch =
    reportTypeValue === "SECTION_111_INVOICING" &&
    showReportTable &&
    !isLoadingReportData;

  return (
    <>
      <ReportStatus
        isLoading={isLoadingReportData}
        lastRunTime={lastRunTime}
        reportType={reportType}
        reportMeta={reportMeta}
        showReportTable={showReportTable}
      />
      {canShowInvoiceListBatch &&
        reportData &&
        selectedRows &&
        setSelectedRows && (
          <BatchInvoiceActions
            selectedRows={selectedRows}
            reportData={reportData}
            onClearSelection={() => setSelectedRows(new Set())}
            onUpdateSelection={setSelectedRows}
            autoSelectEligible={true}
          />
        )}
      {canShowSection111Batch &&
        reportData &&
        selectedRows &&
        setSelectedRows && (
          <Section111InvoiceActions
            selectedRows={selectedRows}
            reportData={reportData}
            reportMeta={reportMeta}
            totalRecords={totalRecords ?? 0}
            onClearSelection={() => setSelectedRows(new Set())}
          />
        )}
    </>
  );
};

export default FinanceReportHeaderStatus;
